/* eslint-disable max-lines */
import PropTypes from 'prop-types';

import CmsBlock from 'Component/CmsBlock';
import Loader from 'Component/Loader';
import ProductListPage from 'Component/ProductListPage';
import {
    ProductList as SourceProductList
} from 'SourceComponent/ProductList/ProductList.component';
import history from 'Util/History';

/** @namespace Bodypwa/Component/ProductList/Component */
export class ProductListComponent extends SourceProductList {
    static propTypes = {
        ...SourceProductList.propTypes,
        isSwiper: PropTypes.bool
    };

    static defaultProps = {
        ...SourceProductList.defaultProps,
        isSwiper: false
    };

    // eslint-disable-next-line @scandipwa/scandipwa-guidelines/only-render-in-component
    componentDidUpdate() {
        const {
            isInfiniteLoaderEnabled
        } = this.props;

        if (isInfiniteLoaderEnabled) {
            this.observePageChange();
        }
    }

    // eslint-disable-next-line @scandipwa/scandipwa-guidelines/only-render-in-component
    observePageChange() {
        const { updatePage, isLoading } = this.props;
        if (isLoading) {
            this.pagesIntersecting = [];
        }

        if (!this.observer && 'IntersectionObserver' in window) {
            const threshold = this._getThreshold();

            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(({ target, isIntersecting }) => {
                    const page = +Object.keys(this.nodes).find((node) => this.nodes[node] === target);
                    const index = this.pagesIntersecting.indexOf(page);

                    if (isIntersecting && index === -1) {
                        this.pagesIntersecting.push(page);
                    }

                    if (!isIntersecting && index > -1) {
                        this.pagesIntersecting.splice(index, 1);
                    }
                });

                const minPage = Math.min(...this.pagesIntersecting);

                if (minPage < Infinity) {
                    // TODO check this for page navigation
                    updatePage(minPage);
                }
            }, {
                rootMargin: '0px',
                threshold
            });
        }
        this.updateObserver();
    }

    renderPages() {
        const {
            pages,
            isVisible,
            isLoading,
            isInfiniteLoaderEnabled
        } = this.props;

        if (isLoading) {
            return this.renderPage();
        }

        const pageRenders = Object.entries(pages).map(this.renderProductPage);

        if (isVisible && isInfiniteLoaderEnabled) { // add placeholders to the end of pages if needed
            // TODO: investigate issue with lodder not showing
            // const key = Math.max(Object.keys(pages)) + 1; // the key should match next page key
            // pageRenders.push(this.renderPage({ key }));
        }

        return pageRenders;
    }

    renderScrollReference(next) {
        const { loadPrevBtn, emptyRef } = this.props;
        return (
            <div
              data-scroll-refrence="true"
              ref={ !next
                  ? loadPrevBtn : emptyRef }
            />
        );
    }

    renderLoadButton(next = true) {
        const {
            isShowLoading,
            isInfiniteLoaderEnabled,
            loadPrevPage,
            loadPage,
            totalPages,
            isPageLoading,
            pages,
            totalItems,
            pageSize
        } = this.props;
        const label = next ? __('Load More') : __('Load previous');
        const itemsLoaded = Object.keys(pages)[Object.keys(pages).length - 1] * pageSize;
        if (!totalPages || (next && itemsLoaded >= totalItems)
         || ((!isShowLoading || !isInfiniteLoaderEnabled) && !next)) {
            return this.renderScrollReference(next);
        }

        if (isPageLoading) {
            return (
                <>
                    <div
                      block="ProductList"
                      elem="LoadButton"
                      className="Button"
                      role="button"
                      tabIndex="0"
                    >
                        <Loader isLoading={ isPageLoading } isButton />
                    </div>
                    { this.renderScrollReference(next) }
                </>
            );
        }

        return (
            <>
                <div
                  block="ProductList"
                  elem="LoadButton"
                  className="Button"
                  role="button"
                  tabIndex="0"
                  onKeyUp={ next ? loadPage : loadPrevPage }
                  onClick={ next ? loadPage : loadPrevPage }
                >
                    { label }
                </div>
                { this.renderScrollReference(next) }
            </>
        );
    }

    renderProductsViewedCount() {
        const {
            pages, totalItems, totalPages, pageSize
        } = this.props;

        if (!totalPages) {
            return false;
        }
        const itemsLoaded = Object.keys(pages)[Object.keys(pages).length - 1] * pageSize;
        const products = itemsLoaded > totalItems ? totalItems : itemsLoaded;
        return (
            <div
              block="ProductList"
              elem="ProductsViewed"
              role="button"
              tabIndex="0"
            >
                { __('You’ve viewed %s of %s products', products, totalItems) }
            </div>
        );
    }

    renderPage(props = {}) {
        const {
            isInfiniteLoaderEnabled,
            loadPage,
            isLoading,
            isVisible,
            mix,
            isSwiper
        } = this.props;
        const {
            items,
            keys,
            pageNumber,
            selectedFilters,
            wrapperRef,
            key
        } = this._processProps(props);

        return (
            <ProductListPage
              isSwiper={ isSwiper }
              key={ key }
              isInfiniteLoaderEnabled={ isInfiniteLoaderEnabled }
              updatePages={ loadPage }
              isLoading={ isLoading }
              isVisible={ isVisible }
              mix={ mix }
              items={ items }
              keys={ keys }
              pageNumber={ pageNumber }
              selectedFilters={ selectedFilters }
              wrapperRef={ wrapperRef }
            />
        );
    }

    renderNoProducts() {
        const {
            location: {
                pathname
            }
        } = history;
        const isSearchPage = pathname.includes('search');

        if (isSearchPage) {
            return <CmsBlock identifier="pwa_error_page_sliders" />;
        }

        return (
            <div block="ProductList">
                <div
                  block="ProductList"
                  elem="ProductsMissing"
                >
                    <h2>{ __('We are sorry!') }</h2>
                    <h3>{ __('There were no products found matching your request.') }</h3>
                    <p>{ __('Please, try removing selected filters and try again!') }</p>
                </div>
            </div>
        );
    }

    render() {
        const {
            totalPages,
            isLoading,
            mix
        } = this.props;

        if (!isLoading && totalPages === 0) {
            return this.renderNoProducts();
        }

        return (
            <div
              block="ProductList"
              mods={ { isLoading } }
              mix={ mix }
            >
                { this.renderTitle() }
                { this.renderLoadButton(false) }
                { this.renderPages() }
                { this.renderProductsViewedCount() }
                { this.renderLoadButton(true) }
            </div>
        );
    }
}

export default ProductListComponent;
